import { lighten, saturate } from 'polished';

// ============================================================================
// PALETA DE MARCA — FONTE UNICA DE VERDADE
// ============================================================================
//
// Para alterar qualquer cor do sistema, edite os valores abaixo. Tudo no
// projeto consome desta paleta (light.js, dark.js, assets/themes,
// utils/GetColor, etc).
//
// Estrutura:
//   - brand: cores conceituais (azul/vermelho/verde/etc) — base unica
//   - light: variante para modo claro (sobrescreve backgrounds, neutros)
//   - dark:  variante para modo escuro (saturate/lighten + neutros dark)
//
// As cores de marca aparecem em AMBOS os modos (luz/escuro). Apenas
// backgrounds, surfaces e textos diferem entre os modos.
// ============================================================================

// ----- Cores brutas (brand) — edite aqui para mudar a identidade visual ------
//
// Semantica:
//   AZUL  = identidade do SISTEMA (chrome geral) + EBAs (bombas/agua)
//   LARANJA = UCHAs (turbogeradores, energia)
//   VERMELHO/AMARELO/VERDE/CINZA = status semanticos invariaveis
//
// Triplet padronizado (dark/normal/light) tanto pra azul quanto pra laranja
// permite hover/pressed states + backgrounds sutis consistentes nas duas
// familias. NUNCA usar hex hardcoded em estilo de componente — sempre via
// props.theme.colors.XYZ ou import { themes } from 'assets' (estatico).

const brand = {
  // --- Azuis (SISTEMA + EBA) ---
  // darkerblue: texto primario modo claro (alias semantico textPrimary)
  // darkblue:   azul royal pra headers/CTAs/badges importantes
  // blue:       accent brand vibrante (links, CTAs, EBA accent)
  // lightblue:  hover/selecao/halo, badges info, ring de selecao
  darkerblue: '#1E2944',
  darkblue: '#192B80',
  blue: '#089FD8',
  lightblue: '#3BBFF1',

  // --- Vermelhos (status: alarme/erro) ---
  red: '#EB6D70',
  darkred: '#801919',
  pink: '#DA667B',

  // --- Verdes (status: ok/rodando) ---
  green: '#1AB858',
  watergreen: '#63CCA6',
  lightgreen: '#72F0A4',

  // --- Amarelo (status: atencao) ---
  yellow: '#F6DE60',

  // --- Laranjas (UCHA) — triplet pra paridade com azul ---
  // darkorange:  hover/pressed, contornos UCHA destacados
  // orange:      accent UCHA principal (TagTitle, badges, botoes UCHA)
  // lightorange: backgrounds sutis (tint behind cards/banners UCHA)
  darkorange: '#C26A30',
  orange: '#F69A60',
  lightorange: '#FBC79A',

  // Roxos (extra)
  purple: '#BD4FC2',
  lightpurple: '#AB95AC',

  // Neutros invariantes
  white: '#FFFFFF',
  black: '#000000',
  label: '#B8B8B8',
  darkgray: '#54545C',

  // Status (gauge ranges) — paleta especifica para os GaugeChart
  statusN: '#67DC95',
  statusW: '#B0DF70',
  statusE: '#F6DE60',
  statusNormal: '#72F0A4',
  statusAtencao: '#F69A60',
  statusErro: '#EB6D70',

  // Heatmap (intensidade crescente de alarme em grafos historicos)
  graphIntensity1: '#EB5757',
  graphIntensity2: '#F17D7F',
  graphIntensity3: '#F88E91',
  graphIntensity4: '#FABABC',
  graphIntensity5: '#FCD7D8',
};

// ----- Variante Light --------------------------------------------------------

const light = {
  ...brand,

  // Backgrounds / superficies
  background: '#F3F1F1',
  card: '#FFFFFF',
  surfaceMuted: '#F2F2F2',
  lightgrey: '#FAFAFA',
  lightergrey: '#F2F2F2',

  // Bordas / divisores
  disabled: '#E3E9EB',
  border: '#E3E9EB',

  // Overlay / hover sutil
  overlay: 'rgba(0, 0, 0, 0.5)',
  hover: 'rgba(0, 0, 0, 0.04)',

  // Foco
  focus: brand.blue,

  // Textos (semantico)
  textPrimary: brand.darkerblue,
  textMuted: brand.label,
  textInverse: brand.white,
};

// ----- Variante Dark ---------------------------------------------------------
//
// As cores de marca (azul/vermelho/etc.) ficam ligeiramente mais saturadas
// pra compensar a perda de luminosidade no fundo escuro. Backgrounds e
// textos sao invertidos.

const dark = {
  ...brand,
  darkerblue: lighten(0.8, brand.darkerblue),
  darkblue: lighten(0.3, brand.darkblue),
  blue: saturate(0.3, brand.blue),
  lightblue: saturate(0.3, brand.lightblue),
  red: saturate(0.1, brand.red),
  darkorange: saturate(0.3, brand.darkorange),
  orange: saturate(0.3, brand.orange),
  lightorange: saturate(0.3, brand.lightorange),
  green: saturate(0.5, brand.green),
  watergreen: saturate(0.3, brand.watergreen),
  lightgreen: saturate(0.3, brand.lightgreen),
  yellow: saturate(0.1, brand.yellow),
  purple: saturate(0.3, brand.purple),
  lightpurple: saturate(0.3, brand.lightpurple),
  pink: saturate(0.3, brand.pink),

  // Backgrounds / superficies (DARK)
  background: '#181818',
  card: '#202020',
  darker: '#191919',
  surfaceMuted: '#2A2A2A',
  lightgrey: '#3A3A3A',
  lightergrey: '#2A2A2A',

  // Bordas / divisores
  disabled: '#444444',
  border: '#444444',

  // Overlay / hover sutil
  overlay: 'rgba(0, 0, 0, 0.6)',
  hover: 'rgba(255, 255, 255, 0.06)',

  // Foco
  focus: saturate(0.3, brand.blue),

  // Textos (semantico)
  textPrimary: lighten(0.8, brand.darkerblue),
  textMuted: brand.label,
  textInverse: brand.white,
};

// ============================================================================
// Exports
// ============================================================================

// Paleta para uso fora do ThemeProvider (SVG, helpers, getColor utils).
// Usa as cores do modo claro por padrao — cores de marca como
// red/green/orange sao iguais em ambos os modos entao funciona p/ ambos.
//
// IMPORTANTE: se voce precisar da cor exata do modo ATIVO em runtime,
// use props.theme.colors.xxx dentro de styled-components (que reage ao
// ThemeProvider). Esta export e estatica.
export const themes = light;

const palette = {
  brand,
  light,
  dark,
};

export default palette;
